/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { css } from 'styled-components';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { FormattedMessage } from 'react-intl';
import DateFormatter from 'Common/Components/DateFormatter';
import {
  ColKbDescription, ColKbReferences,
  ColKbResolution, ColKbPolicyViolations, ColKbCreator,
  ColKbCreatedDate, Col
} from './styled';
import { CheckHeader } from './CheckHeader/CheckHeader';
import { CheckColum } from './CheckColumn/CheckColum';
import NameCell from './NameCell';
import { SeverityHeader } from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/columns';

TimeAgo.addLocale(en);

/**
 * Knowledge Base (vuln templates) table.
 * ctx: `{ isFetching, fields }`
 *
 * Widths/visibility/order come from `fields` WITHOUT optional chaining (as the legacy columns
 * did): a missing field throws the same way.
 */
const knowledgeBaseSchema = {
  id: 'knowledgeBase',
  loading: { mockRows: 13, skeleton: ({ isFetching }) => isFetching },
  ordering: {},
  styles: css`
  overflow-x: auto;

  .rt-thead .rt-tr .rt-th,
  .rt-tbody .rt-tr .rt-td {
    flex: 0 0 auto !important;
  }

  .rt-thead,
  .rt-tbody {
    width: max-content;
    min-width: 100% !important;
  }
`,
  columns: [{
    header: () => <CheckHeader />,
    id: 'selected',
    accessor: 'Selected',
    visible: true,
    sortable: false,
    resizable: false,
    maxWidth: 38,
    minWidth: 38,
    style: { display: 'flex' },
    cell: (rowInfo) => <CheckColum template={ rowInfo.original } />,
    order: -1
  }, {
    header: () => <SeverityHeader />,
    showSortIcon: false,
    headerStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    accessor: 'exploitation',
    visible: ({ fields }) => fields.exploitation.visible,
    maxWidth: 45,
    minWidth: 45,
    order: ({ fields }) => fields.exploitation.order,
    id: 'severity',
    resizable: false,
    cell: (rowInfo) => (
      <Col>
        <div className={ `background-${rowInfo.original.exploitation === 'info' ? 'informational' : (rowInfo.original.exploitation === 'med' ? 'medium' : rowInfo.original.exploitation)}` }>
          <div className="severity-letter">{rowInfo.original.exploitation ? rowInfo.original.exploitation.charAt(0).toUpperCase() : ''}</div>
        </div>
      </Col>
    ),
    cellProps: () => ({ 'column-type': 'exploitation', 'style': { display: 'flex', justifyContent: 'center', alignItems: 'center' } })
  }, {
    header: () => <FormattedMessage id="Name" />,
    id: 'name',
    resizable: true,
    visible: ({ fields }) => fields.name.visible,
    width: ({ fields }) => fields.name.defaultWidth || fields.name.defaultMin,
    maxWidth: ({ fields }) => fields.name.maxWidth,
    minWidth: ({ fields }) => fields.name.minWidth,
    order: ({ fields }) => fields.name.order,
    cell: (rowInfo) => <NameCell rowInfo={ rowInfo.original } />,
    cellProps: () => ({ 'column-type': 'name' })
  }, {
    header: () => <FormattedMessage id="description" />,
    id: 'description',
    resizable: true,
    visible: ({ fields }) => fields.description.visible,
    width: ({ fields }) => fields.description.defaultWidth || fields.description.defaultMin,
    maxWidth: ({ fields }) => fields.description.maxWidth,
    minWidth: ({ fields }) => fields.description.minWidth,
    order: ({ fields }) => fields.description.order,
    cell: (rowInfo) => (
      <ColKbDescription>
        { rowInfo.original.description }
      </ColKbDescription>
    ),
    cellProps: () => ({ 'column-type': 'description' })
  }, {
    header: () => <FormattedMessage id="references" />,
    id: 'references',
    resizable: true,
    visible: ({ fields }) => fields.references.visible,
    width: ({ fields }) => fields.references.defaultWidth || fields.references.defaultMin,
    maxWidth: ({ fields }) => fields.references.maxWidth,
    minWidth: ({ fields }) => fields.references.minWidth,
    order: ({ fields }) => fields.references.order,
    cell: (rowInfo) => (
      <ColKbReferences>
        { rowInfo.original.references }
      </ColKbReferences>
    ),
    cellProps: () => ({ 'column-type': 'references' })
  }, {
    header: () => <FormattedMessage id="resolution" />,
    id: 'resolution',
    resizable: true,
    visible: ({ fields }) => fields.resolution.visible,
    width: ({ fields }) => fields.resolution.defaultWidth || fields.resolution.defaultMin,
    maxWidth: ({ fields }) => fields.resolution.maxWidth,
    minWidth: ({ fields }) => fields.resolution.minWidth,
    order: ({ fields }) => fields.resolution.order,
    cell: (rowInfo) => (
      <ColKbResolution>
        { rowInfo.original.resolution }
      </ColKbResolution>
    ),
    cellProps: () => ({ 'column-type': 'resolution' })
  }, {
    header: () => <FormattedMessage id="policyviolations" />,
    id: 'policyviolations',
    resizable: true,
    sortable: false,
    visible: ({ fields }) => fields.policyviolations.visible,
    width: ({ fields }) => fields.policyviolations.defaultWidth || fields.policyviolations.defaultMin,
    maxWidth: ({ fields }) => fields.policyviolations.maxWidth,
    minWidth: ({ fields }) => fields.policyviolations.minWidth,
    order: ({ fields }) => fields.policyviolations.order,
    cell: (rowInfo) => (
      <ColKbPolicyViolations>
        { rowInfo.original.policyviolations?.map((policy, index) => {
          const { policyviolations } = rowInfo.original;
          if (policyviolations?.length > 1 && index + 1 < policyviolations?.length) return `${policy}, `;
          return `${policy}`;
        }) }
      </ColKbPolicyViolations>
    ),
    cellProps: () => ({ 'column-type': 'creator' })
  }, {
    header: () => <FormattedMessage id="creator" />,
    id: 'creator__username',
    resizable: true,
    visible: ({ fields }) => fields.creator_id.visible,
    width: ({ fields }) => fields.creator_id.defaultWidth || fields.creator_id.defaultMin,
    maxWidth: ({ fields }) => fields.creator_id.maxWidth,
    minWidth: ({ fields }) => fields.creator_id.minWidth,
    order: ({ fields }) => fields.creator_id.order,
    cell: (rowInfo) => (
      <ColKbCreator>
        { rowInfo.original.creator }
      </ColKbCreator>
    ),
    cellProps: () => ({ 'column-type': 'creator' })
  }, {
    header: () => <FormattedMessage id="created_date" />,
    id: 'create_date',
    resizable: true,
    width: ({ fields }) => fields.create_date.defaultWidth || fields.create_date.defaultMin,
    visible: ({ fields }) => fields.create_date.visible,
    maxWidth: ({ fields }) => fields.create_date.maxWidth,
    minWidth: ({ fields }) => fields.create_date.minWidth,
    order: ({ fields }) => fields.create_date.order,
    cell: (rowInfo) => (
      <ColKbCreatedDate>
        { rowInfo.original.create_at ? <DateFormatter date={ rowInfo.original.create_at } /> : '-'}
      </ColKbCreatedDate>
    ),
    cellProps: () => ({ 'column-type': 'created_date' })
  }]
};

export default knowledgeBaseSchema;
